import { apiClient } from './apiClient';
import type {
  EmployeeWorkflowAssignment,
  AssignWorkflowRequest,
  BulkAssignWorkflowRequest,
  BulkAssignWorkflowResponse,
  WorkflowAssignmentHistory,
  ApprovalWorkflow,
} from '@/types/workflow-assignment';

/**
 * Get the workflow assignment for an employee
 */
export async function getEmployeeWorkflowAssignment(
  employeeId: number
): Promise<EmployeeWorkflowAssignment> {
  return await apiClient.get<EmployeeWorkflowAssignment>(
    `/employees/${employeeId}/approval-workflow`
  );
}

/**
 * Assign a workflow to an employee
 */
export async function assignWorkflowToEmployee(
  employeeId: number,
  request: AssignWorkflowRequest
): Promise<{ success: boolean; message: string; assignment: EmployeeWorkflowAssignment }> {
  return await apiClient.post<{
    success: boolean;
    message: string;
    assignment: EmployeeWorkflowAssignment;
  }>(`/employees/${employeeId}/approval-workflow`, request);
}

/**
 * Get workflow assignment history for an employee
 */
export async function getWorkflowAssignmentHistory(
  employeeId: number
): Promise<WorkflowAssignmentHistory[]> {
  return await apiClient.get<WorkflowAssignmentHistory[]>(
    `/employees/${employeeId}/workflow-history`
  );
}

/**
 * Bulk assign workflow to multiple employees
 */
export async function bulkAssignWorkflow(
  request: BulkAssignWorkflowRequest
): Promise<BulkAssignWorkflowResponse> {
  return await apiClient.post<BulkAssignWorkflowResponse>(
    '/employees/bulk-assign-workflow',
    request
  );
}

/**
 * Get all available workflows, optionally filtered to active ones only.
 */
export async function getAllWorkflows(activeOnly: boolean = false): Promise<ApprovalWorkflow[]> {
  const endpoint = activeOnly ? '/workflows?activeOnly=true' : '/workflows';
  return await apiClient.get<ApprovalWorkflow[]>(endpoint);
}

/**
 * Get the default workflow
 */
export async function getDefaultWorkflow(): Promise<ApprovalWorkflow | null> {
  const workflows = await getAllWorkflows();
  return workflows.find((w) => w.isDefault) || null;
}

/**
 * Helper function to determine workflow type
 */
export function getWorkflowType(
  assignment: EmployeeWorkflowAssignment
): 'default' | 'custom' | 'no-approval' {
  if (assignment.workflowId === 'NO_APPROVAL') {
    return 'no-approval';
  }
  if (assignment.isDefault || !assignment.workflowId) {
    return 'default';
  }
  return 'custom';
}

/**
 * Helper function to get workflow icon
 */
export function getWorkflowIcon(type: 'default' | 'custom' | 'no-approval'): string {
  switch (type) {
    case 'default':
      return '🔄';
    case 'custom':
      return '⚙️';
    case 'no-approval':
      return '✅';
  }
}

/**
 * Helper function to get workflow badge color
 */
export function getWorkflowBadgeColor(
  type: 'default' | 'custom' | 'no-approval'
): string {
  switch (type) {
    case 'default':
      return 'bg-info-100 text-info-800 border-info-300';
    case 'custom':
      return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'no-approval':
      return 'bg-success-100 text-success-800 border-success-300';
  }
}

/**
 * Helper function to get workflow card color
 */
export function getWorkflowCardColor(
  type: 'default' | 'custom' | 'no-approval'
): string {
  switch (type) {
    case 'default':
      return 'border-info-200 bg-info-50/50';
    case 'custom':
      return 'border-purple-200 bg-purple-50/50';
    case 'no-approval':
      return 'border-success-200 bg-success-50/50';
  }
}
